import { useEffect, useMemo } from 'react';
import { BookingSource } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { DATETIME_FORMATS } from '../../constants';
import { useBookingSiteStore } from '../../stores/use-booking-site-store';
import { BOOKING_SITE_HTTP_OPTIONS } from '../../utils';
import { endOfWeek } from '../../utils/date-helpers';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Params {
  providerCalendarId: string;
  providerId: string;
}

const MINIMUM_APPT_DURATION_MINUTES = 5;

export const useFetchAndStoreProviderAvailabilities = ({ providerId, providerCalendarId }: Params) => {
  const {
    isSchedularV3Enabled,
    isScheduleDefinitionsEnabled,
    selectedAppointmentType,
    selectedLocationId,
    weekInView,
    setAllProviderAvailabilities,
    selectedLocationTimeZone,
    selectedAppointmentLeadTime,
  } = useBookingSiteStore([
    'isSchedularV3Enabled',
    'isScheduleDefinitionsEnabled',
    'selectedAppointmentType',
    'selectedLocationId',
    'weekInView',
    'setAllProviderAvailabilities',
    'selectedLocationTimeZone',
    'selectedAppointmentLeadTime',
  ]);

  const availabilityQueryV2 = ScheduleQueries.useListAppointmentTypeTimes(
    {
      appointmentTypeId: selectedAppointmentType?.id ?? '',
      locationId: selectedLocationId,
      providerId,
      startDate: weekInView,
      endDate: endOfWeek(weekInView).toISOString(),
    },
    { enabled: !isSchedularV3Enabled },
    BOOKING_SITE_HTTP_OPTIONS
  );

  const { startDateTime, endDateTime } = useMemo(() => {
    const weekDate = dayjs(weekInView).tz(selectedLocationTimeZone);
    // The availability api requires the startDateTime to be at the start of the day (midnight). Lead time needs to be added to the current date and still be at the start of the day. IMPORTANT: Although we need to fetch the entire day of availabilities - we're displaying the slots which are either after the current time or the selectedAppointmentLeadTime.
    return {
      startDateTime: weekDate.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
      endDateTime: endOfWeek(weekInView, selectedLocationTimeZone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
    };
  }, [selectedLocationTimeZone, weekInView]);

  const availabilityQueryV3 = SchedulerV3.Queries.useListCalendarAvailabilitiesQuery({
    request: {
      calendarId: providerCalendarId,
      locationId: selectedLocationId,
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      durationMinutes:
        !selectedAppointmentType?.durationMinutes ||
        selectedAppointmentType?.durationMinutes < MINIMUM_APPT_DURATION_MINUTES
          ? MINIMUM_APPT_DURATION_MINUTES
          : selectedAppointmentType?.durationMinutes,
      requestSource: BookingSource.BOOKING_SITE,
      clientTimezone: dayjs.tz.guess(),
      appointmentTypeId: selectedAppointmentType?.id ?? '',
    },
    options: {
      enabled:
        isSchedularV3Enabled &&
        !!providerCalendarId &&
        !!selectedLocationId &&
        !!weekInView &&
        !isScheduleDefinitionsEnabled,
      retry: 1,
      cacheTime: 1000 * 60 * 3, // 3 minutes
      staleTime: 1000 * 60 * 3, // 3 minutes
    },
    httpOptions: BOOKING_SITE_HTTP_OPTIONS,
    bypassScheduleV3FlagCheck: true,
  });

  const openSlotList = useMemo<string[]>(() => {
    if (isSchedularV3Enabled) {
      return (availabilityQueryV3.data?.availableSlots ?? [])
        .flatMap(({ date, slots }) => {
          return (slots ?? []).map((slot) => {
            return !!date && !!slot.startTime ? formatDate(`${date} ${slot.startTime}`, 'YYYY-MM-DDTHH:mm:ss') : '';
          });
        })
        .filter(
          (slot) =>
            !!slot &&
            dayjs
              .tz(slot, selectedLocationTimeZone)
              .isAfter(dayjs(selectedAppointmentLeadTime).tz(selectedLocationTimeZone))
        )
        .sort((a, b) => dayjs(a).diff(dayjs(b)));
    }
    return (availabilityQueryV2.data?.data ?? [])
      .map((slot) => slot.startTime ?? '')
      .filter((slot) => !!slot && dayjs(slot).isAfter(dayjs(selectedAppointmentLeadTime)));
  }, [
    availabilityQueryV2.data?.data,
    availabilityQueryV3.data?.availableSlots,
    isSchedularV3Enabled,
    selectedAppointmentLeadTime,
    selectedLocationTimeZone,
  ]);

  const isLoading = availabilityQueryV2.isLoading || availabilityQueryV3.isLoading;

  useEffect(() => {
    setAllProviderAvailabilities(providerId, isLoading ? [] : openSlotList);
  }, [openSlotList, isLoading, providerId, setAllProviderAvailabilities]);

  return {
    isLoading,
  };
};
